import { NavLink } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Building2,
  CalendarClock,
  ClipboardCheck,
  ClipboardList,
  FileSearch,
  Film,
  Gauge,
  GraduationCap,
  KeyRound,
  LayoutDashboard,
  Layers,
  Library,
  LifeBuoy,
  LineChart,
  Mail,
  Palette,
  PencilRuler,
  Receipt,
  ShieldCheck,
  Sliders,
  Ticket,
  Trash2,
  Users,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { hasRole } from "@/components/app/roles";
import { Wordmark } from "@/components/app/Logo";
import { getUser } from "@/auth.js";
import { getBrandName } from "@/lib/brandRuntime";

type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  minRole: string;
  examinerOnly?: boolean;
  /** Show only when the user's role matches exactly one of these values. Used
   *  for student-only backend endpoints (e.g. /v1/dashboard/student) which
   *  403 for any other role. */
  exactRoles?: string[];
  /** Active-match exactly this path (don't prefix-match). Set on parents
   *  that have children so the parent only highlights on its own page. */
  end?: boolean;
  /** Nested sub-links rendered indented beneath this item. */
  children?: NavItem[];
};

type NavGroup = { label: string; items: NavItem[] };

const GROUPS: NavGroup[] = [
  {
    label: "Learn",
    items: [
      { to: "/dashboard", label: "Assessments", icon: LayoutDashboard, minRole: "student" },
      {
        to: "/progress",
        label: "My progress",
        icon: LineChart,
        minRole: "student",
        exactRoles: ["student"],
      },
    ],
  },
  {
    label: "Teach",
    items: [
      { to: "/authoring", label: "Authoring", icon: PencilRuler, minRole: "teacher" },
      { to: "/sittings", label: "Sittings", icon: CalendarClock, minRole: "teacher" },
      { to: "/stats", label: "Stats", icon: BarChart3, minRole: "teacher" },
      { to: "/marking", label: "Marking", icon: ClipboardCheck, minRole: "teacher", examinerOnly: true },
    ],
  },
  {
    label: "Organisation",
    items: [
      { to: "/org/members", label: "Members", icon: Users, minRole: "tenant_admin" },
      // Candidate slips: visible to the narrow `registrar` role plus
      // anyone tenant_admin or above. exactRoles narrows the rank
      // filter to the slip-manager allowlist (mirrors the API's
      // requireSlipManager()).
      {
        to: "/org/candidate-slips",
        label: "Entry slips",
        icon: Ticket,
        minRole: "student",
        exactRoles: ["registrar", "tenant_admin", "super_admin"],
      },
      // Exam room codes: same slip-manager allowlist as entry slips (the
      // invigilator running the room reads the code out).
      {
        to: "/org/room-codes",
        label: "Room codes",
        icon: KeyRound,
        minRole: "student",
        exactRoles: ["registrar", "tenant_admin", "super_admin"],
      },
      { to: "/org/marking-workload", label: "Workload", icon: Gauge, minRole: "tenant_admin" },
      { to: "/org/submissions", label: "Submissions", icon: ClipboardList, minRole: "moderator" },
      { to: "/org/replays", label: "Session replays", icon: Film, minRole: "tenant_admin" },
      { to: "/org/audit", label: "Audit log", icon: FileSearch, minRole: "tenant_admin" },
      { to: "/org/centre-documentation", label: "Centre docs", icon: Library, minRole: "tenant_admin" },
      // Billing had NO nav entry: the panels were rendered inside /stats, a
      // page grouped under "Teach" and named after teaching analytics, so an
      // admin had to guess that invoices and the spend cap lived there.
      { to: "/billing", label: "Billing", icon: Receipt, minRole: "tenant_admin" },
      { to: "/org/settings", label: "Settings", icon: Sliders, minRole: "tenant_admin" },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/settings/security", label: "Security", icon: ShieldCheck, minRole: "student" },
      { to: "/settings/sessions", label: "Active sessions", icon: KeyRound, minRole: "student" },
      { to: "/help", label: "Help", icon: LifeBuoy, minRole: "student" },
    ],
  },
  {
    label: "Platform",
    items: [
      { to: "/platform", label: "Tenants", icon: Building2, minRole: "super_admin" },
      { to: "/platform/programmes", label: "Programmes", icon: Layers, minRole: "super_admin" },
      { to: "/platform/metrics", label: "Metrics", icon: GraduationCap, minRole: "super_admin" },
      { to: "/platform/billing", label: "Billing", icon: Receipt, minRole: "super_admin" },
      { to: "/platform/errors", label: "Errors", icon: AlertTriangle, minRole: "super_admin" },
      { to: "/platform/diagnostics", label: "Diagnostics", icon: Activity, minRole: "super_admin" },
      { to: "/platform/branding", label: "Branding", icon: Palette, minRole: "super_admin" },
      { to: "/platform/emails", label: "Email templates", icon: Mail, minRole: "super_admin" },
      {
        to: "/platform/questions",
        label: "Question bank",
        icon: Library,
        minRole: "super_admin",
        end: true,
        children: [
          { to: "/platform/questions/review-queue", label: "Review queue", icon: ClipboardList, minRole: "super_admin" },
          { to: "/platform/questions/moderation", label: "Moderation", icon: AlertTriangle, minRole: "super_admin" },
        ],
      },
      { to: "/platform/audit", label: "Audit log", icon: FileSearch, minRole: "super_admin" },
      { to: "/platform/deletion-requests", label: "Deletion requests", icon: Trash2, minRole: "super_admin" },
    ],
  },
];

function NavRow({
  item,
  nested,
  onNavigate,
}: {
  item: NavItem;
  nested?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      end={item.end ?? (item.to === "/dashboard" || item.to === "/platform")}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-2.5 rounded-lg py-2 transition-colors",
          nested ? "pl-9 pr-3 text-[13px]" : "px-3 text-sm",
          isActive
            ? "bg-accent text-accent-foreground font-medium"
            : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              aria-hidden
              className={cn(
                "absolute left-0 top-1/2 w-[3px] -translate-y-1/2 rounded-r-full bg-primary",
                nested ? "h-4" : "h-5",
              )}
            />
          )}
          <item.icon
            className={cn(
              "shrink-0 transition-colors",
              nested ? "h-3.5 w-3.5" : "h-4 w-4",
              isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground",
            )}
            aria-hidden
          />
          <span>{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

export function SidebarContent({ onNavigate }: { onNavigate?: () => void } = {}) {
  const user = getUser();
  const role = user?.role ?? "student";
  // Matches the API's requireExaminer rule: membership must carry the
  // is_examiner flag, OR the role is moderator (implicit). Tenant admins
  // and super admins don't auto-qualify — hiding the link in their nav
  // avoids tempting them into a 403 path.
  const isExaminer = !!user?.is_examiner || role === "moderator";

  const canSee = (item: NavItem) => {
    if (!hasRole(role, item.minRole)) return false;
    if (item.examinerOnly && !isExaminer) return false;
    if (item.exactRoles && !item.exactRoles.includes(role)) return false;
    return true;
  };

  const visibleGroups = GROUPS.map((group) => ({
    ...group,
    items: group.items
      .filter(canSee)
      .map((item) =>
        item.children ? { ...item, children: item.children.filter(canSee) } : item,
      ),
  })).filter((g) => g.items.length > 0);

  return (
    <nav aria-label="Primary" className="flex h-full flex-col gap-6 py-5">
      <div className="px-4">
        <NavLink
          to="/"
          onClick={onNavigate}
          aria-label={`${getBrandName()} home`}
          className="group inline-flex items-center transition-transform hover:scale-[1.02]"
        >
          <Wordmark size="md" />
        </NavLink>
        <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Functional Skills
        </p>
      </div>

      <div className="flex-1 space-y-5">
        {visibleGroups.map((group) => (
          <div key={group.label} className="px-2">
            <div className="mb-1.5 px-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/80">
              {group.label}
            </div>
            <ul className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavRow item={item} onNavigate={onNavigate} />
                  {item.children && item.children.length > 0 && (
                    <ul className="mt-0.5 flex flex-col gap-0.5">
                      {item.children.map((child) => (
                        <li key={child.to}>
                          <NavRow item={child} nested onNavigate={onNavigate} />
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {user && (
        <div className="mx-2 rounded-xl border border-border/60 bg-muted/40 p-3 text-xs">
          <div className="truncate font-medium text-foreground">
            {user.full_name || user.email.split("@")[0]}
          </div>
          <div className="truncate text-[11px] text-muted-foreground">{user.email}</div>
          {user.role && (
            <div className="mt-1 inline-flex items-center rounded-full bg-background px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {user.role.replace("_", " ")}
            </div>
          )}
        </div>
      )}
    </nav>
  );
}

export function Sidebar() {
  return (
    <aside
      data-role="sidebar"
      className="hidden w-64 shrink-0 border-r border-border/60 bg-background/70 backdrop-blur-sm lg:flex lg:flex-col"
    >
      <SidebarContent />
    </aside>
  );
}
